/**
 * Auth context — session Supabase + profil app.users.
 *
 * Chunk 7.D : on enrichit `AuthTenant` avec les champs de présentation
 * (`branding`, `logo_url`, `timezone`, `currency`) désormais retournés par
 * `GET /v1/auth/me` (cf. patch backend `apps/api/src/routes/auth.ts`).
 *
 * Le branding est appliqué au boot via `applyBranding(tenant.branding)` :
 *   - immédiatement après chaque `loadProfile()` réussi
 *   - réinitialisé lors d'un signOut
 *
 * Le helper `applyBranding` est idempotent et accepte `null|undefined` pour
 * un reset complet. Voir `lib/branding.ts`.
 */

import { Schemas, type Membership, type QuoteDeliveryPolicy } from "@kollectivia/shared";
import type { Session } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { api } from "../lib/api";
import { applyBranding, type Branding } from "../lib/branding";
import { supabase } from "../lib/supabase";
import {
  hasTenantWrites,
  onTenantScopeChange,
  resetTenantScope,
  setTenantScope,
  tenantScope,
} from "../lib/tenantScope";

export type AuthUser = {
  id: string;
  email: string;
  tenant_id: string | null;
  role: "super_admin" | "tenant_admin" | "broker" | "client_contact";
  full_name: string | null;
  locale: "fr" | "en";
  avatar_url?: string | null;
};

export type AuthTenant = {
  quote_delivery_policy?: QuoteDeliveryPolicy;
  id: string;
  slug: string;
  name: string;
  locale: "fr" | "en";
  plan: string;
  status: string;
  /** Couleurs CSS + logo_url. Voir BrandingSchema (shared). Null = thème par défaut. */
  branding: Branding | null;
  logo_url: string | null;
  timezone: string | null;
  currency: string | null;
};

type MembershipStatus = "active" | "pending" | "disabled" | "unavailable";
type Me = {
  user: AuthUser;
  tenant: AuthTenant | null;
  memberships: Membership[];
  membership_status: MembershipStatus;
};
type AuthState = {
  session: Session | null;
  user: AuthUser | null;
  tenant: AuthTenant | null;
  loading: boolean;
  memberships: Membership[];
  membershipStatus: MembershipStatus;
  contextRevision: number;
  contextError: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
  switchTenant: (tenantId: string | null) => boolean;
};
const AuthContext = createContext<AuthState | null>(null);
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const sessionRef = useRef<Session | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [contextError, setContextError] = useState(false);
  const [contextRevision, setContextRevision] = useState(0);
  const requestId = useRef(0);
  const mounted = useRef(true);
  const loadProfile = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setContextError(false);
    try {
      const result = await api.get<Me>("/v1/auth/me");
      if (!mounted.current || id !== requestId.current) return;
      result.memberships = Schemas.MembershipSchema.array().parse(result.memberships);
      // Pin the server's default once. Later requests never silently fall back to another cabinet.
      if (!tenantScope() && result.user.role !== "super_admin" && result.user.tenant_id) {
        if (setTenantScope(result.user.tenant_id)) return;
      }
      setMe(result);
      applyBranding(result.tenant?.branding ?? null);
    } catch {
      if (!mounted.current || id !== requestId.current) return;
      setMe(null);
      setContextError(true);
      applyBranding(null);
    } finally {
      if (mounted.current && id === requestId.current) setLoading(false);
    }
  }, []);
  useEffect(() => {
    mounted.current = true;
    let active = true;
    const unsubscribe = onTenantScopeChange(() => {
      requestId.current += 1;
      setMe(null);
      applyBranding(null);
      setContextRevision((v) => v + 1);
      void queryClient.cancelQueries();
      queryClient.clear();
      if (sessionRef.current) void loadProfile();
    });
    const applySession = (next: Session | null) => {
      const prior = sessionRef.current;
      sessionRef.current = next;
      setSession(next);
      if (!next) {
        requestId.current += 1;
        resetTenantScope();
        setMe(null);
        setLoading(false);
        setContextError(false);
        applyBranding(null);
        queryClient.clear();
      } else if (!prior || prior.user.id !== next.user.id) {
        if (prior) resetTenantScope();
        void loadProfile();
      }
    };
    let authEvent = false;
    void supabase.auth.getSession().then(({ data }) => {
      if (active && !authEvent) applySession(data.session);
    });
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, next) => {
      authEvent = true;
      // Never await a Supabase call from its auth-state callback (SDK lock).
      queueMicrotask(() => {
        if (active) applySession(next);
      });
    });
    return () => {
      active = false;
      mounted.current = false;
      sessionRef.current = null;
      requestId.current += 1;
      unsubscribe();
      subscription.subscription.unsubscribe();
    };
  }, [loadProfile, queryClient]);
  const switchTenant = useCallback(
    (tenantId: string | null) => {
      if (queryClient.isMutating() || hasTenantWrites()) return false;
      return setTenantScope(tenantId);
    },
    [queryClient],
  );
  async function signIn(email: string, password: string) {
    const result = await api.post<{ access_token: string; refresh_token: string }>(
      "/v1/auth/login",
      { email, password },
      { noAuth: true },
    );
    setLoading(true);
    const { error } = await supabase.auth.setSession(result);
    if (error) {
      setLoading(false);
      throw error;
    }
  }
  async function signOut() {
    try {
      await api.post("/v1/auth/logout", {});
    } finally {
      sessionRef.current = null;
      requestId.current += 1;
      resetTenantScope();
      queryClient.clear();
      setMe(null);
      setSession(null);
      setLoading(false);
      setContextError(false);
      applyBranding(null);
      await supabase.auth.signOut();
    }
  }
  const value: AuthState = {
    session,
    user: me?.user ?? null,
    tenant: me?.tenant ?? null,
    loading,
    memberships: me?.memberships ?? [],
    membershipStatus: me?.membership_status ?? "unavailable",
    contextRevision,
    contextError,
    signIn,
    signOut,
    refresh: loadProfile,
    switchTenant,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth doit être utilisé dans <AuthProvider>");
  return context;
}
